import * as React from 'react';
import { Typography } from 'omakase';
import { ifClickable } from '@tock/shared/utils/touchUtils';
import BaseComponent from '@tock/shared/components/BaseComponent';
import clsx from 'clsx';

export interface Props {
  date: string;
  focused: boolean;
  handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, day: string): void;
  isTabbable: boolean;
  isAvailable?: boolean;
  isClosed: boolean;
  isEnabled: boolean;
  isLocked: boolean;
  isNotAvailable: boolean;
  isOutOfMonth: boolean;
  isPast: boolean;
  isFuture: boolean;
  isToday: boolean;
  isSelected: boolean;
  isSoldOut: boolean;
  onClick(date: string): void;
  onTabAway?(): void;
  text: string;
}

export default class ConsumerCalendarDay extends BaseComponent<Props> {
  buttonRef: React.RefObject<HTMLButtonElement>;
  initialFocusTimeoutId?: number;

  constructor(props: Props) {
    super(props);
    this.buttonRef = React.createRef<HTMLButtonElement>();
  }

  componentDidMount() {
    if (this.props.focused) {
      // A short delay helps Safari + VoiceOver reliably move focus
      // into popup-mounted calendar content.
      this.initialFocusTimeoutId = window.setTimeout(() => {
        this.buttonRef.current?.focus();
        this.initialFocusTimeoutId = undefined;
      }, 50);
    }
  }

  componentDidUpdate(prevProps: Props) {
    this.cancelFocusCallbackIfNecessary(this.props.focused, prevProps.focused);
    this.focusIfNecessary(this.props.focused, prevProps.focused);
  }

  componentWillUnmount() {
    if (this.initialFocusTimeoutId) {
      window.clearTimeout(this.initialFocusTimeoutId);
      this.initialFocusTimeoutId = undefined;
    }
  }

  handleClick(e: React.SyntheticEvent) {
    e.preventDefault();
    this.props.onClick(this.props.date);
  }

  handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    const { onTabAway, handleKeyDown, date } = this.props;
    if (onTabAway && event.key === 'Tab') {
      onTabAway();
    } else {
      handleKeyDown(event, date);
    }
  }

  focusIfNecessary(nowFocused: boolean, prevFocused: boolean) {
    if (nowFocused && !prevFocused && this.buttonRef.current) {
      this.buttonRef.current.focus();
    }
  }

  cancelFocusCallbackIfNecessary(nowFocused: boolean, prevFocused: boolean) {
    if (!nowFocused && prevFocused && this.initialFocusTimeoutId) {
      window.clearTimeout(this.initialFocusTimeoutId);
      this.initialFocusTimeoutId = undefined;
    }
  }

  render() {
    const {
      isAvailable,
      isEnabled,
      isFuture,
      isLocked,
      isOutOfMonth,
      isPast,
      isSelected,
      isSoldOut,
      isTabbable,
      isToday,
      text,
      date,
    } = this.props;

    const className = clsx('ConsumerCalendar-day', {
      'is-out-of-month': isOutOfMonth,
      'is-in-month': !isOutOfMonth,
      'is-past': isPast,
      'is-future': isFuture,
      'is-selected': isSelected,
      'is-available': isAvailable && isEnabled,
      'is-locked': isLocked,
      'is-sold': isSoldOut,
      'is-disabled': !isEnabled,
      'is-today': isToday,
    });
    const disableForKeyboardNav = isOutOfMonth || isPast || !isEnabled;
    const hasNoAvailability = isSoldOut || (!isEnabled && !isOutOfMonth && !isPast && !isFuture);
    const ariaLabel = hasNoAvailability ? `${date}, no availability` : date;
    return (
      <div aria-selected={isSelected} role="gridcell">
        <button
          aria-disabled={disableForKeyboardNav}
          aria-label={ariaLabel}
          className={className}
          disabled={disableForKeyboardNav}
          tabIndex={isTabbable ? undefined : -1}
          onClick={isEnabled ? ifClickable(this.handleClick, 'date') : undefined}
          onKeyDown={this.handleKeyDown}
          ref={this.buttonRef}
          data-testid="consumer-calendar-day"
        >
          <Typography color="inherit" component="span" variant="body02">
            {text}
          </Typography>
        </button>
      </div>
    );
  }
}
